import { useState } from "react";
import { jsPDF } from "jspdf";
import { addPdfFooter } from "../pdf/footer";

export type PlanTask = {
  stage: string;
  week: string;
  title: string;
  description: string;
  due_on: string;
  gift_code: string;
};
export type Discipler = { name: string; church_role: string };

type Result = {
  gift: { code: string; name: string; next_step: string };
  score: number;
  level: string;
};

const dueDate = (days: number) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
};

export const suggestedPlan = (results: Result[]): PlanTask[] => {
  const first = results[0]?.gift;
  const second = results[1]?.gift || first;
  if (!first) return [];
  return [
    {
      stage: "1. Compreender",
      week: "Semanas 1 e 2",
      title: `Estudar e conversar sobre ${first.name}`,
      description: `Leia referências bíblicas e orientações sobre ${first.name}; converse com uma liderança e identifique uma oportunidade segura de serviço.`,
      due_on: dueDate(14),
      gift_code: first.code,
    },
    {
      stage: "2. Experimentar",
      week: "Semanas 3 a 6",
      title: `Realizar duas experiências supervisionadas de ${first.name}`,
      description: `Combine duas ações pequenas, registre contexto, pessoas atendidas, resultado e dificuldade encontrada.`,
      due_on: dueDate(42),
      gift_code: first.code,
    },
    {
      stage: "3. Receber feedback",
      week: "Semanas 7 e 8",
      title: `Solicitar três feedbacks sobre ${first.name}`,
      description: `Peça retorno objetivo de uma liderança, de um colega e, quando aplicável, de uma pessoa beneficiada.`,
      due_on: dueDate(56),
      gift_code: first.code,
    },
    {
      stage: "4. Consolidar",
      week: "Semanas 9 a 12",
      title: `Definir rotina de serviço em ${second.name}`,
      description: `Escolha frequência, mentor, capacitação e uma data de reavaliação para transformar a experiência em desenvolvimento contínuo.`,
      due_on: dueDate(84),
      gift_code: second.code,
    },
  ];
};

const ministryMatches: Record<string, string[]> = {
  ADM: ['Administração, Planejamento e Gestão da Igreja', 'Secretaria da Igreja', 'Comissão de Finanças', 'Comissão de Construção, Reforma, Manutenção e Patrimônio'],
  APO: ['Evangelismo, Missão e Ação Comunitária', 'Pequenos Grupos', 'Classes Bíblicas e Preparação para o Batismo'],
  CON: ['Escola Sabatina', 'Educação e Educação Cristã', 'Biblioteca, Publicações e Literatura'],
  DIS: ['Anciãos', 'Capelania e Atendimento Espiritual', 'Atendimento a Interessados, Visitantes e Novos Membros'],
  ENS: ['Escola Sabatina', 'Escola Sabatina Infantil', 'Classes Bíblicas e Preparação para o Batismo', 'Educação e Educação Cristã'],
  ENC: ['Ministério de Adolescentes', 'Ministério Jovem', 'Ministério de Jovens Adultos e Universitários'],
  EVA: ['Evangelismo, Missão e Ação Comunitária', 'Ministério Pessoal', 'Classes Bíblicas e Preparação para o Batismo'],
  FE: ['Ministério de Oração e Intercessão', 'Evangelismo, Missão e Ação Comunitária', 'Pequenos Grupos'],
  GEN: ['Ação Solidária Adventista — ASA', 'Mordomia Cristã', 'Tesouraria'],
  HOS: ['Ministério da Recepção e Hospitalidade', 'Cerimonial, Organização de Cultos e Coordenação de Cerimônias', 'Cozinha, Alimentação e Apoio a Eventos'],
  INT: ['Ministério de Oração e Intercessão', 'Ministério de Visitação e Apoio Hospitalar', 'Pequenos Grupos'],
  LID: ['Anciãos', 'Ministério Jovem', 'Clube de Desbravadores', 'Administração, Planejamento e Gestão da Igreja'],
  MER: ['Ação Solidária Adventista — ASA', 'Ministério de Apoio a Cuidadores, Familiares e Enlutados', 'Ministério de Visitação e Apoio Hospitalar'],
  MIS: ['Evangelismo, Missão e Ação Comunitária', 'Serviço Voluntário Adventista — SVA', 'Ministério Pessoal'],
  PAS: ['Integração, Discipulado e Conservação de Membros', 'Ministério da Família', 'Ministério de Visitação e Apoio Hospitalar'],
  PRO: ['Espírito de Profecia', 'Liberdade Religiosa', 'Evangelismo, Missão e Ação Comunitária'],
  SAB: ['Anciãos', 'Comissão de Finanças', 'Administração, Planejamento e Gestão da Igreja'],
  SER: ['Diaconato', 'Limpeza, Jardinagem, Conservação e Zeladoria', 'Transporte e Apoio Logístico', 'Portaria, Segurança, Estacionamento e Controle de Acesso'],
  SOC: ['Ação Solidária Adventista — ASA', 'Ministério Adventista das Possibilidades — MAP', 'Ministério de Inclusão, Acessibilidade e Libras'],
  SAU: ['Saúde e Alimentação Saudável', 'Ministério de Combate às Dependências', 'Ministério Adventista das Possibilidades — MAP'],
};

const recommendedMinistries = (results: Result[]) => [...new Set(results.slice(0, 5).flatMap(result => ministryMatches[result.gift.code] || []))].slice(0, 7);

function buildPdf(
  name: string,
  profile: Record<string, string>,
  results: Result[],
  tasks: PlanTask[],
  confidence: { level: string; score: number },
  disciplers: Discipler[],
) {
  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  pdf.setFillColor(16, 42, 67);
  pdf.rect(0, 0, 210, 38, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(21);
  pdf.text("DONS ESPIRITUAIS", 16, 18);
  pdf.setFontSize(10);
  pdf.text(
    "Francisney Liberato | Inventário, resultados e Plano de Ação",
    16,
    28,
  );
  pdf.setTextColor(31, 41, 51);
  pdf.setFontSize(13);
  pdf.text(`Participante: ${name || "Participante"}`, 16, 51);
  pdf.setFontSize(10);
  pdf.text(
    `Confiança do resultado: ${confidence.level} (${confidence.score}%)`,
    16,
    59,
  );
  pdf.text(
    `Experiência informada: ${profile.service_experience || "não informada"}`,
    16,
    66,
    { maxWidth: 176 },
  );
  let y = 80;
  pdf.setFontSize(14);
  pdf.text("Resultados e compatibilidades atuais", 16, y);
  y += 10;
  results.slice(0, 5).forEach((item, index) => {
    pdf.setTextColor(31, 41, 51);
    pdf.setFontSize(10);
    pdf.text(`${index + 1}. ${item.gift.name}`, 16, y);
    pdf.setFillColor(234, 243, 238);
    pdf.roundedRect(70, y - 4, 88, 7, 2, 2, "F");
    pdf.setFillColor(23, 107, 92);
    pdf.roundedRect(
      70,
      y - 4,
      Math.max(3, (88 * item.score) / 100),
      7,
      2,
      2,
      "F",
    );
    pdf.setTextColor(31, 41, 51);
    pdf.text(`${item.score}% — ${item.level}`, 163, y);
    y += 12;
  });
  y += 4;
  pdf.setFontSize(14);
  pdf.text("Plano de Ação confirmado", 16, y);
  y += 9;
  tasks.forEach((task, index) => {
    pdf.setFontSize(10);
    pdf.setTextColor(31, 41, 51);
    pdf.text(
      `${index + 1}. ${task.title} — prazo: ${new Date(`${task.due_on}T12:00:00`).toLocaleDateString("pt-BR")}`,
      16,
      y,
      { maxWidth: 178 },
    );
    y += 5;
    pdf.setTextColor(82, 99, 109);
    pdf.setFontSize(8.5);
    pdf.text(task.description, 20, y, { maxWidth: 172 });
    y += 11;
  });
  pdf.addPage();
  pdf.setFillColor(16, 42, 67);
  pdf.rect(0, 0, 210, 38, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(20);
  pdf.text("DONS ESPIRITUAIS", 16, 18);
  pdf.setFontSize(9.5);
  pdf.text("Áreas sugeridas e Plano de Ação de 90 dias", 16, 28);
  pdf.setTextColor(16, 42, 67);
  pdf.setFontSize(14);
  pdf.text("Áreas de serviço sugeridas para experimentar", 16, 52);
  let detailY = 61;
  pdf.setFontSize(9);
  pdf.setTextColor(82, 99, 109);
  pdf.text("Sugestões indicativas para conversa com a liderança local e serviço supervisionado.", 16, detailY, { maxWidth: 176 });
  detailY += 9;
  recommendedMinistries(results).forEach((ministry, index) => {
    pdf.setTextColor(31, 41, 51);
    pdf.text(`${index + 1}. ${ministry}`, 20, detailY, { maxWidth: 170 });
    detailY += 6;
  });
  pdf.setTextColor(82, 99, 109);
  pdf.setFontSize(7.5);
  pdf.text("Observação: a existência, a denominação e a estrutura das áreas podem variar conforme a realidade e as orientações da igreja local.", 16, detailY + 3, { maxWidth: 178 });
  detailY += 18;
  if (disciplers.length) {
    pdf.setTextColor(16, 42, 67);
    pdf.setFontSize(13);
    pdf.text("Discipuladores indicados", 16, detailY);
    detailY += 8;
    pdf.setTextColor(82, 99, 109);
    pdf.setFontSize(9);
    disciplers.forEach((item, index) => { pdf.text(`${index + 1}. ${item.name} — ${item.church_role}`, 20, detailY, { maxWidth: 170 }); detailY += 6; });
    detailY += 6;
  }
  pdf.setTextColor(16, 42, 67);
  pdf.setFontSize(14);
  pdf.text("Plano de Ação de 90 dias", 16, detailY);
  detailY += 10;
  tasks.forEach((task, index) => {
    if (detailY > 244) { pdf.addPage(); pdf.setFillColor(16, 42, 67); pdf.rect(0, 0, 210, 32, "F"); pdf.setTextColor(255, 255, 255); pdf.setFontSize(17); pdf.text("DONS ESPIRITUAIS", 16, 18); detailY = 46; }
    pdf.setFillColor(248, 250, 249);
    pdf.roundedRect(16, detailY - 5, 178, 20, 2, 2, "F");
    pdf.setTextColor(23, 107, 92);
    pdf.setFontSize(8.5);
    pdf.text(`${task.stage} • ${task.week}`, 20, detailY);
    pdf.setTextColor(16, 42, 67);
    pdf.setFontSize(10);
    pdf.text(`${index + 1}. ${task.title}`, 20, detailY + 6, { maxWidth: 140 });
    pdf.setTextColor(82, 99, 109);
    pdf.setFontSize(8);
    pdf.text(`Prazo: ${task.due_on ? new Date(`${task.due_on}T12:00:00`).toLocaleDateString("pt-BR") : "a definir"}`, 20, detailY + 12);
    detailY += 24;
  });
  addPdfFooter(
    pdf,
    "Relatório indicativo. Avance com constância: prática, frutos e comunidade confirmam o desenvolvimento.",
  );
  return pdf;
}

export function InventoryCompletion({
  name,
  profile,
  results,
  confidence,
  confirmed,
  onConfirm,
  onExecution,
  onNotice,
}: {
  name: string;
  profile: Record<string, string>;
  results: Result[];
  confidence: { level: string; score: number };
  confirmed: boolean;
  onConfirm: (tasks: PlanTask[], disciplers: Discipler[]) => Promise<boolean>;
  onExecution: () => void;
  onNotice: (message: string) => void;
}) {
  const [tasks, setTasks] = useState(() => suggestedPlan(results));
  const ministries = recommendedMinistries(results);
  const [manualMinistries, setManualMinistries] = useState(['', '', '']);
  const [discipler, setDiscipler] = useState<Discipler[]>([{ name: '', church_role: '' }, { name: '', church_role: '' }, { name: '', church_role: '' }]);
  const allPlanTasks = () => [
    ...tasks,
    ...manualMinistries.filter((ministry) => ministry.trim()).map((ministry, index) => ({
      stage: 'Exploração pessoal', week: 'A definir com a liderança', title: `Conhecer e experimentar: ${ministry.trim()}`,
      description: `Converse com a liderança local e realize uma experiência supervisionada em ${ministry.trim()}, registrando aprendizados e próximos passos.`,
      due_on: dueDate(21 + index * 7), gift_code: '',
    })),
  ];
  const update = (index: number, key: keyof PlanTask, value: string) =>
    setTasks((items) =>
      items.map((task, i) => (i === index ? { ...task, [key]: value } : task)),
    );
  const download = () =>
    buildPdf(name, profile, results, allPlanTasks(), confidence, discipler.filter(item => item.name.trim() || item.church_role.trim())).save(
      "inventario-e-plano-de-acao-dons-espirituais.pdf",
    );
  const share = async () => {
    const pdf = buildPdf(name, profile, results, allPlanTasks(), confidence, discipler.filter(item => item.name.trim() || item.church_role.trim()));
    const file = new File(
      [pdf.output("blob")],
      "inventario-e-plano-de-acao-dons-espirituais.pdf",
      { type: "application/pdf" },
    );
    if (
      navigator.share &&
      (!navigator.canShare || navigator.canShare({ files: [file] }))
    ) {
      try {
        await navigator.share({
          title: "Dons Espirituais — Inventário e Plano de Ação",
          text: "Meu inventário e Plano de Ação de Dons Espirituais.",
          files: [file],
        });
        return;
      } catch {
        return;
      }
    }
    download();
    onNotice(
      "O navegador não permite compartilhar PDF diretamente. O arquivo foi baixado para envio pelo WhatsApp ou e-mail.",
    );
  };
  return (
    <section className="completion-overlay">
      <div className="completion-sheet">
        <span className="completion-kicker">INVENTÁRIO CONCLUÍDO</span>
        <h1>Seu resultado e Plano de Ação</h1>
        <p className="completion-lead">
          Estes resultados são indicativos. Ajuste as tarefas abaixo de acordo
          com sua realidade, confirme o plano e registre a execução.
        </p>
        <div className="confidence">
          <div>
            <b>
              Índice de confiança: {confidence.level} ({confidence.score}%)
            </b>
            <p>
              O nível considera respostas, coerência e evidências já
              registradas.
            </p>
          </div>
        </div>
        <div className="community-chart" id="journey-result">
          <h2>Gráfico de compatibilidades atuais</h2>
          {results.slice(0, 5).map((result) => (
            <div className="bar-row" key={result.gift.code}>
              <span>{result.gift.name}</span>
              <i>
                <b style={{ width: `${result.score}%` }} />
              </i>
              <strong>{result.score}%</strong>
            </div>
          ))}
        </div>
        <div className="completion-results">
          {results.slice(0, 2).map((result, i) => (
            <article key={result.gift.code}>
              <b>{i === 0 ? "Predominante" : "Complementar"}</b>
              <h2>{result.gift.name}</h2>
              <p>{result.gift.next_step}</p>
            </article>
          ))}
        </div>
        <div className="callout ministry-recommendations" id="journey-areas">
          <b>Áreas de serviço sugeridas para experimentar</b>
          <p>Com base nas compatibilidades atuais, estas são sugestões de experimentação, conversa com a liderança e serviço supervisionado — não uma nomeação automática.</p>
          <ol>{ministries.map((ministry) => <li key={ministry}>{ministry}</li>)}</ol>
          <small className="ministry-note"><b>Observação:</b> A existência, a denominação e a estrutura de cada ministério, departamento, coordenação, comissão ou serviço podem variar conforme a realidade, o tamanho, as necessidades e as orientações de cada igreja local.</small>
        </div>
        <div className="callout ministry-recommendations">
          <b>Outras áreas que você deseja conhecer (opcional)</b>
          <p>Você pode incluir até três ministérios, departamentos ou coordenações que deseja experimentar. Eles serão acrescentados ao seu Plano de Ação para conversa e validação com a liderança.</p>
          <div className="form-grid">{manualMinistries.map((ministry, index) => <label className="field" key={index}><span>{index + 1}ª Área desejada</span><input value={ministry} placeholder="Ex.: Ministério da Música" onChange={(event) => setManualMinistries(items => items.map((item, itemIndex) => itemIndex === index ? event.target.value : item))}/></label>)}</div>
          <h3 className="discipler-heading">Discipuladores que acompanharão seu desenvolvimento (opcional)</h3>
          <p>Informe até três pessoas que poderão orientar, incentivar e acompanhar sua jornada.</p>
          <div className="discipler-grid">{discipler.map((item, index) => <div className="discipler-card" key={index}><b>Discipulador {index + 1}</b><label className="field"><span>Nome Completo</span><input value={item.name} placeholder="Ex.: João da Silva" autoComplete="name" onChange={(event) => setDiscipler(items => items.map((current, currentIndex) => currentIndex === index ? { ...current, name: event.target.value } : current))}/></label><label className="field"><span>Função na Igreja</span><input value={item.church_role} placeholder="Ex.: Ancião, Pastor ou Líder" onChange={(event) => setDiscipler(items => items.map((current, currentIndex) => currentIndex === index ? { ...current, church_role: event.target.value } : current))}/></label></div>)}</div>
        </div>
        <h2 className="section-title" id="journey-plan">
          Plano de Ação sugerido — ajuste antes de confirmar
        </h2>
        <p className="hint">
          Os prazos são contados a partir de hoje. Você pode editar título,
          tarefa e prazo; depois da confirmação, use Execução para alimentar o
          progresso.
        </p>
        <div className="plan-editor">
          {tasks.map((task, index) => (
            <article key={`${task.stage}-${index}`}>
              <span>{task.week}</span>
              <b>{task.stage}</b>
              <label>
                Tarefa
                <input
                  value={task.title}
                  onChange={(event) =>
                    update(index, "title", event.target.value)
                  }
                />
              </label>
              <label>
                Detalhamento
                <textarea
                  value={task.description}
                  onChange={(event) =>
                    update(index, "description", event.target.value)
                  }
                />
              </label>
              <label>
                Prazo
                <input
                  type="date"
                  value={task.due_on}
                  onChange={(event) =>
                    update(index, "due_on", event.target.value)
                  }
                />
              </label>
            </article>
          ))}
        </div>
        <div className="pager">
          <button className="button outline" onClick={download}>
            Salvar PDF completo
          </button>
          <button className="button outline" onClick={share}>
            Compartilhar PDF
          </button>
          {confirmed ? (
            <button className="button" onClick={onExecution}>
              Ir para Execução
            </button>
          ) : (
            <button
              className="button"
              onClick={async () => {
                if (await onConfirm(allPlanTasks(), discipler.filter(item => item.name.trim() || item.church_role.trim())))
                  onNotice(
                    "Plano de Ação confirmado. Agora registre cada tarefa na aba Execução.",
                  );
              }}
            >
              Confirmar meu Plano de Ação
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

export function ExecutionWorkspace({
  goals,
  onSave,
  onCreateManual,
  onNotice,
}: {
  goals: any[];
  onSave: (payload: Record<string, string>) => Promise<boolean>;
  onCreateManual: (payload: Record<string, string>) => Promise<boolean>;
  onNotice: (message: string) => void;
}) {
  const initial = {
    goal_id: "",
    occurred_on: new Date().toISOString().slice(0, 10),
    progress: "0",
    people_served: "0",
    minutes_served: "0",
    activity: "",
    context: "",
    outcome: "",
    feedback: "",
    difficulties: "",
    learning: "",
    next_step: "",
  };
  const [form, setForm] = useState<Record<string, string>>(initial);
  const [saveFeedback, setSaveFeedback] = useState<{ type: "error" | "success"; text: string } | null>(null);
  const [manual, setManual] = useState(false);
  const [manualGoal, setManualGoal] = useState<Record<string, string>>({
    title: "",
    action_text: "",
    due_on: "",
    frequency: "",
    result_indicator: "",
    evidence_required: "",
  });
  const activeGoals = goals.filter((goal) => goal.status !== "completed");
  const selectedGoal = goals.find((goal) => goal.id === form.goal_id);
  const submit = async () => {
    const missing = [
      !form.goal_id && "tarefa do Plano de Ação",
      !form.occurred_on && "data da execução",
      !form.activity.trim() && "o que foi executado",
      !form.context.trim() && "contexto da atividade",
      !form.outcome.trim() && "resultado observado",
      !form.feedback.trim() && "feedback ou comprovação de terceiros",
    ].filter(Boolean) as string[];
    if (missing.length) {
      const text = `Antes de salvar, preencha: ${missing.join(", ")}.`;
      setSaveFeedback({ type: "error", text });
      onNotice(text);
      return;
    }
    if (await onSave(form)) {
      setSaveFeedback({ type: "success", text: "Execução salva com sucesso. O andamento da tarefa e o painel Meu Desenvolvimento foram atualizados." });
      setForm({ ...initial, goal_id: form.goal_id });
    } else {
      setSaveFeedback({ type: "error", text: "Não foi possível salvar a execução. Revise os campos e tente novamente." });
    }
  };
  const createManual = async () => {
    if (!manualGoal.title || !manualGoal.action_text || !manualGoal.due_on) {
      onNotice(
        "Para criar uma ação manual, informe título, ação verificável e prazo.",
      );
      return;
    }
    if (await onCreateManual(manualGoal)) {
      setManual(false);
      setManualGoal({
        title: "",
        action_text: "",
        due_on: "",
        frequency: "",
        result_indicator: "",
        evidence_required: "",
      });
    }
  };
  return (
    <section className="execution-workspace">
      <HeaderText
        title="Execução do Plano de Ação"
        text="Aqui está o Plano de Ação aprovado no Inventário. Escolha uma tarefa, informe o que foi realizado e registre a comprovação para acompanhar o planejado e o executado."
      />
      <div className="plan-status">
        {goals.length ? (
          goals.map((goal) => (
            <article key={goal.id}>
              <b>{goal.title}</b>
              <span>
                Prazo:{" "}
                {goal.due_on
                  ? new Date(`${goal.due_on}T12:00:00`).toLocaleDateString(
                      "pt-BR",
                    )
                  : "a definir"}{" "}
                · andamento: {goal.progress || 0}% ·{" "}
                {goal.status === "completed"
                  ? "concluída"
                  : "em acompanhamento"}
              </span>
              {goal.description && <small>{goal.description}</small>}
            </article>
          ))
        ) : (
          <p>
            O Plano de Ação ainda não foi localizado. Entre em Meu
            Desenvolvimento com o mesmo WhatsApp e senha usados no Inventário;
            após a confirmação, as tarefas aprovadas aparecem aqui
            automaticamente.
          </p>
        )}
      </div>
      <div className="form-grid">
        <label className="field">
          <span>Tarefa confirmada do Plano de Ação</span>
          <select
            value={form.goal_id}
            onChange={(event) =>
              setForm({ ...form, goal_id: event.target.value })
            }
          >
            <option value="">Selecione uma tarefa</option>
            {activeGoals.map((goal) => (
              <option key={goal.id} value={goal.id}>
                {goal.title}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Data da execução</span>
          <input
            type="date"
            value={form.occurred_on}
            onChange={(event) =>
              setForm({ ...form, occurred_on: event.target.value })
            }
          />
        </label>
        <label className="field">
          <span>Progresso confirmado (0 a 100%)</span>
          <input
            type="number"
            min="0"
            max="100"
            value={form.progress}
            onChange={(event) =>
              setForm({ ...form, progress: event.target.value })
            }
          />
        </label>
        {selectedGoal?.description && (
          <div className="callout">
            <b>Detalhamento da tarefa selecionada</b>
            <p>{selectedGoal.description}</p>
          </div>
        )}
        <label className="field">
          <span>Contexto da atividade</span>
          <input
            value={form.context}
            onChange={(event) =>
              setForm({ ...form, context: event.target.value })
            }
          />
        </label>
        <label className="field">
          <span>Pessoas atendidas</span>
          <input
            type="number"
            min="0"
            value={form.people_served}
            onChange={(event) =>
              setForm({ ...form, people_served: event.target.value })
            }
          />
        </label>
        <label className="field">
          <span>Minutos dedicados</span>
          <input
            type="number"
            min="0"
            value={form.minutes_served}
            onChange={(event) =>
              setForm({ ...form, minutes_served: event.target.value })
            }
          />
        </label>
        <label className="field">
          <span>O que foi executado</span>
          <textarea
            value={form.activity}
            onChange={(event) =>
              setForm({ ...form, activity: event.target.value })
            }
          />
        </label>
        <label className="field">
          <span>Resultado observado</span>
          <textarea
            value={form.outcome}
            onChange={(event) =>
              setForm({ ...form, outcome: event.target.value })
            }
          />
        </label>
        <label className="field">
          <span>Feedback ou comprovação de terceiros</span>
          <textarea
            placeholder="Nome, função e síntese do retorno recebido"
            value={form.feedback}
            onChange={(event) =>
              setForm({ ...form, feedback: event.target.value })
            }
          />
        </label>
        <label className="field">
          <span>Dificuldade encontrada</span>
          <textarea
            value={form.difficulties}
            onChange={(event) =>
              setForm({ ...form, difficulties: event.target.value })
            }
          />
        </label>
        <label className="field">
          <span>Aprendizado pessoal</span>
          <textarea
            value={form.learning}
            onChange={(event) =>
              setForm({ ...form, learning: event.target.value })
            }
          />
        </label>
        <label className="field">
          <span>Próximo passo</span>
          <textarea
            value={form.next_step}
            onChange={(event) =>
              setForm({ ...form, next_step: event.target.value })
            }
          />
        </label>
      </div>
      {saveFeedback && <p className={saveFeedback.type === "success" ? "execution-feedback success" : "execution-feedback error"} role="status">{saveFeedback.text}</p>}
      <button
        className="button"
        onClick={submit}
        disabled={!activeGoals.length}
      >
        Salvar execução vinculada ao plano
      </button>
      <div className="manual-action">
        <button className="button outline" onClick={() => setManual(!manual)}>
          {manual ? "Cancelar ação manual" : "Criar ação manual na Execução"}
        </button>
        {manual && (
          <div className="form-grid">
            <label className="field">
              <span>Título da nova ação</span>
              <input
                value={manualGoal.title}
                onChange={(event) =>
                  setManualGoal({ ...manualGoal, title: event.target.value })
                }
              />
            </label>
            <label className="field">
              <span>Ação verificável</span>
              <input
                value={manualGoal.action_text}
                onChange={(event) =>
                  setManualGoal({
                    ...manualGoal,
                    action_text: event.target.value,
                  })
                }
              />
            </label>
            <label className="field">
              <span>Prazo</span>
              <input
                type="date"
                value={manualGoal.due_on}
                onChange={(event) =>
                  setManualGoal({ ...manualGoal, due_on: event.target.value })
                }
              />
            </label>
            <label className="field">
              <span>Frequência</span>
              <input
                value={manualGoal.frequency}
                onChange={(event) =>
                  setManualGoal({
                    ...manualGoal,
                    frequency: event.target.value,
                  })
                }
              />
            </label>
            <label className="field">
              <span>Indicador de resultado</span>
              <input
                value={manualGoal.result_indicator}
                onChange={(event) =>
                  setManualGoal({
                    ...manualGoal,
                    result_indicator: event.target.value,
                  })
                }
              />
            </label>
            <label className="field">
              <span>Evidência exigida</span>
              <input
                value={manualGoal.evidence_required}
                onChange={(event) =>
                  setManualGoal({
                    ...manualGoal,
                    evidence_required: event.target.value,
                  })
                }
              />
            </label>
            <button className="button" onClick={createManual}>
              Salvar ação manual
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

function HeaderText({ title, text }: { title: string; text: string }) {
  return (
    <header className="heading">
      <span>PRÁTICA COM PROPÓSITO</span>
      <h1>{title}</h1>
      <p>{text}</p>
    </header>
  );
}
